/**
 * iotDemoData.js — IOT-DEMO-SEED-1
 * Canonical IoT demo fixtures for tenantId = 'kairos-demo'.
 * FOUNDATION-PURITY-1: demo fixtures isolated from production foundation.
 *
 * Exports:
 *   KAIROS_DEMO_TRACKERS      — 4 IOTK pucks (iot_trackers collection)
 *   KAIROS_DEMO_TRUCKS        — 4 trucks (trucks collection)
 *   KAIROS_DEMO_TRAILERS      — 4 trailers (assets collection)
 *   KAIROS_DEMO_TRACKER_EVENTS — 144 readings for IOTK-001 (24h breach scenario)
 *   KAIROS_DEMO_THRESHOLDS    — 8 threshold configurations (iot_thresholds collection)
 *   KAIROS_DEMO_ALERTS        — 10 alert fixtures
 *   KAIROS_DEMO_ELD_DRIVERS   — 4 driver HOS records (fleet_drivers collection)
 *   KAIROS_DEMO_ASSETS        — Combined trackers + trailers for Device Management
 */

import { DEMO_TENANT_ID } from './tenantDefaults.js';

const TID = DEMO_TENANT_ID;

// ── Helpers ──────────────────────────────────────────────────────────────────
// Dynamic timestamps so fixtures stay fresh in 24h/7d filter windows
function msAgo(hours, minutes = 0) {
  return new Date(Date.now() - (hours * 3600 + minutes * 60) * 1000).toISOString();
}
function msDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString();
}

// ── IoT Trackers (IOTK-001..004) ─────────────────────────────────────────────
export const KAIROS_DEMO_TRACKERS = [
  {
    id: 'iotk-001-kd', tenantId: TID,
    trackerId: 'IOTK-001', deviceId: 'IOTK-001',
    deviceType: 'Reefer Multi-Sensor', type: 'multi_sensor',
    serial: 'KD-SN-4401', firmwareVersion: '3.2.1',
    loadId: 'WC-10255', customerId: 'wild-fork',
    customerName: 'Wild Fork Foods', productDescription: 'Frozen Wagyu Beef',
    status: 'active', alertStatus: 'breach',
    batteryPercent: 78,
    currentLocation: { lat: 28.5383, lng: -81.3792, city: 'Orlando', state: 'FL' },
    sensorReadings: {
      temperature: { value: 52.0, unit: 'F', status: 'breach', setpointMin: 28, setpointMax: 36 },
      humidity: { percent: 67, status: 'normal', setpointMin: 58, setpointMax: 72 },
      shock: { gforce: 0.09, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(3),
    lastHeartbeat: msAgo(0, 8),
    lastPingAt: msAgo(0, 8),
    lastReadingAt: msAgo(0, 8),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(3), createdBy: 'system',
    updatedAt: msAgo(0, 8), updatedBy: 'system',
  },
  {
    id: 'iotk-002-kd', tenantId: TID,
    trackerId: 'IOTK-002', deviceId: 'IOTK-002',
    deviceType: 'Dry Van Multi-Sensor', type: 'multi_sensor',
    serial: 'KD-SN-4402', firmwareVersion: '3.2.1',
    loadId: 'WC-10262', customerId: 'steve-madden',
    customerName: 'Steve Madden', productDescription: 'Footwear - Retail Replenishment',
    status: 'active', alertStatus: 'normal',
    batteryPercent: 85,
    currentLocation: { lat: 32.7767, lng: -96.7970, city: 'Dallas', state: 'TX' },
    sensorReadings: {
      temperature: { value: 68.4, unit: 'F', status: 'normal', setpointMin: 59, setpointMax: 77 },
      humidity: { percent: 53, status: 'normal', setpointMin: 40, setpointMax: 70 },
      shock: { gforce: 0.12, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(2),
    lastHeartbeat: msAgo(0, 5),
    lastPingAt: msAgo(0, 5),
    lastReadingAt: msAgo(0, 5),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(2), createdBy: 'system',
    updatedAt: msAgo(0, 5), updatedBy: 'system',
  },
  {
    id: 'iotk-003-kd', tenantId: TID,
    trackerId: 'IOTK-003', deviceId: 'IOTK-003',
    deviceType: 'Pharma Cold Chain', type: 'multi_sensor',
    serial: 'KD-SN-4403', firmwareVersion: '3.1.8',
    loadId: 'WC-10265', customerId: 'mckesson',
    customerName: 'McKesson Medical', productDescription: 'Medical Equipment',
    status: 'active', alertStatus: 'warning',
    batteryPercent: 92,
    currentLocation: { lat: 33.4484, lng: -112.0740, city: 'Phoenix', state: 'AZ' },
    sensorReadings: {
      temperature: { value: 39.8, unit: 'F', status: 'warning', setpointMin: 35, setpointMax: 46 },
      humidity: { percent: 57, status: 'normal', setpointMin: 30, setpointMax: 60 },
      shock: { gforce: 0.07, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(4),
    lastHeartbeat: msAgo(0, 12),
    lastPingAt: msAgo(0, 12),
    lastReadingAt: msAgo(0, 12),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(4), createdBy: 'system',
    updatedAt: msAgo(0, 12), updatedBy: 'system',
  },
  {
    id: 'iotk-004-kd', tenantId: TID,
    trackerId: 'IOTK-004', deviceId: 'IOTK-004',
    deviceType: 'Produce Cold Chain', type: 'multi_sensor',
    serial: 'KD-SN-4404', firmwareVersion: '3.1.8',
    loadId: 'WC-10247', customerId: 'produce-co',
    customerName: 'Produce Co', productDescription: 'Fresh Produce - Mixed',
    status: 'offline', alertStatus: 'critical',
    batteryPercent: 21,
    currentLocation: { lat: 35.1495, lng: -90.0490, city: 'Memphis', state: 'TN' },
    sensorReadings: {
      temperature: { value: 36.2, unit: 'F', status: 'normal', setpointMin: 34, setpointMax: 40 },
      humidity: { percent: 84, status: 'normal', setpointMin: 70, setpointMax: 95 },
      shock: { gforce: 0.0, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(5),
    lastHeartbeat: msAgo(1, 22),
    lastPingAt: msAgo(1, 22),
    lastReadingAt: msAgo(1, 22),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(5), createdBy: 'system',
    updatedAt: msAgo(1, 22), updatedBy: 'system',
  },
  // ── IOT-AEGIS-DEMO-READINESS-1: +3 trackers for 15-active-device demo total ──
  {
    id: 'iotk-005-kd', tenantId: TID,
    trackerId: 'IOTK-005', deviceId: 'IOTK-005',
    deviceType: 'Reefer Multi-Sensor', type: 'multi_sensor',
    serial: 'KD-SN-4405', firmwareVersion: '3.2.1',
    loadId: 'WC-10271', customerId: 'sysco',
    customerName: 'Sysco Foods', productDescription: 'Dairy - Cold Chain',
    status: 'active', alertStatus: 'normal',
    batteryPercent: 81,
    currentLocation: { lat: 36.1627, lng: -86.7816, city: 'Nashville', state: 'TN' },
    sensorReadings: {
      temperature: { value: 33.4, unit: 'F', status: 'normal', setpointMin: 32, setpointMax: 38 },
      humidity: { percent: 64, status: 'normal', setpointMin: 55, setpointMax: 75 },
      shock: { gforce: 0.06, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(2),
    lastHeartbeat: msAgo(0, 6),
    lastPingAt: msAgo(0, 6),
    lastReadingAt: msAgo(0, 6),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(2), createdBy: 'system',
    updatedAt: msAgo(0, 6), updatedBy: 'system',
  },
  {
    id: 'iotk-006-kd', tenantId: TID,
    trackerId: 'IOTK-006', deviceId: 'IOTK-006',
    deviceType: 'Dry Van Multi-Sensor', type: 'multi_sensor',
    serial: 'KD-SN-4406', firmwareVersion: '3.2.0',
    loadId: 'WC-10283', customerId: 'whole-foods',
    customerName: 'Whole Foods', productDescription: 'Packaged Goods - Ambient',
    status: 'active', alertStatus: 'warning',
    batteryPercent: 67,
    currentLocation: { lat: 39.7392, lng: -104.9903, city: 'Denver', state: 'CO' },
    sensorReadings: {
      temperature: { value: 71.2, unit: 'F', status: 'normal', setpointMin: 59, setpointMax: 77 },
      humidity: { percent: 48, status: 'normal', setpointMin: 40, setpointMax: 70 },
      shock: { gforce: 0.11, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(1),
    lastHeartbeat: msAgo(0, 9),
    lastPingAt: msAgo(0, 9),
    lastReadingAt: msAgo(0, 9),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(1), createdBy: 'system',
    updatedAt: msAgo(0, 9), updatedBy: 'system',
  },
  {
    id: 'iotk-007-kd', tenantId: TID,
    trackerId: 'IOTK-007', deviceId: 'IOTK-007',
    deviceType: 'Pharma Cold Chain', type: 'multi_sensor',
    serial: 'KD-SN-4407', firmwareVersion: '3.2.1',
    loadId: 'WC-10290', customerId: 'cardinal-health',
    customerName: 'Cardinal Health', productDescription: 'Vaccines - Cold Chain',
    status: 'active', alertStatus: 'normal',
    batteryPercent: 94,
    currentLocation: { lat: 41.8781, lng: -87.6298, city: 'Chicago', state: 'IL' },
    sensorReadings: {
      temperature: { value: 38.7, unit: 'F', status: 'normal', setpointMin: 35, setpointMax: 46 },
      humidity: { percent: 52, status: 'normal', setpointMin: 30, setpointMax: 60 },
      shock: { gforce: 0.05, severity: 'none' },
      light: { lux: 0, status: 'sealed' },
    },
    assignedAt: msDaysAgo(2),
    lastHeartbeat: msAgo(0, 4),
    lastPingAt: msAgo(0, 4),
    lastReadingAt: msAgo(0, 4),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(2), createdBy: 'system',
    updatedAt: msAgo(0, 4), updatedBy: 'system',
  },
];

// ── Trucks (RT-1042, RT-1043, DT-2018, DT-2019) ───────────────────────────────
export const KAIROS_DEMO_TRUCKS = [
  {
    id: 'rt-1042-kd', tenantId: TID,
    unitNumber: 'RT-1042', truckNumber: 'RT-1042',
    type: 'reefer_truck', make: 'Freightliner', model: 'Cascadia',
    year: 2023, vin: '1FUJHLDR8NLHA1042',
    status: 'active', hosStatus: 'driving',
    driverName: 'D. Martinez', driverId: 'DOT-44218',
    activeLoadId: 'WC-10702',
    currentLocation: { lat: 29.7604, lng: -95.3698, city: 'Houston', state: 'TX' },
    currentSpeed: 62,
    fuelLevel: 74,
    mileage: 187420,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(60), createdBy: 'system',
    updatedAt: msAgo(0, 15), updatedBy: 'system',
  },
  {
    id: 'rt-1043-kd', tenantId: TID,
    unitNumber: 'RT-1043', truckNumber: 'RT-1043',
    type: 'reefer_truck', make: 'Kenworth', model: 'T680',
    year: 2022, vin: '1XKYDP9X4NJ531043',
    status: 'active', hosStatus: 'on_duty',
    driverName: 'K. Patterson', driverId: 'DOT-44219',
    activeLoadId: 'WC-10698',
    currentLocation: { lat: 33.4484, lng: -112.0740, city: 'Phoenix', state: 'AZ' },
    currentSpeed: 0,
    fuelLevel: 88,
    mileage: 142800,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(60), createdBy: 'system',
    updatedAt: msAgo(1, 0), updatedBy: 'system',
  },
  {
    id: 'dt-2018-kd', tenantId: TID,
    unitNumber: 'DT-2018', truckNumber: 'DT-2018',
    type: 'dry_truck', make: 'Peterbilt', model: '389',
    year: 2021, vin: '1XPBDP9X5MD552018',
    status: 'active', hosStatus: 'driving',
    driverName: 'J. Rivera', driverId: 'DOT-44220',
    activeLoadId: 'WC-10711',
    currentLocation: { lat: 36.1627, lng: -86.7816, city: 'Nashville', state: 'TN' },
    currentSpeed: 58,
    fuelLevel: 61,
    mileage: 224190,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msAgo(0, 10), updatedBy: 'system',
  },
  {
    id: 'dt-2019-kd', tenantId: TID,
    unitNumber: 'DT-2019', truckNumber: 'DT-2019',
    type: 'dry_truck', make: 'Volvo', model: 'VNL',
    year: 2023, vin: '4V4NC9GH8LN842019',
    status: 'idle', hosStatus: 'off_duty',
    driverName: 'M. Chen', driverId: 'DOT-44221',
    activeLoadId: null,
    currentLocation: { lat: 34.7465, lng: -92.2896, city: 'Little Rock', state: 'AR' },
    currentSpeed: 0,
    fuelLevel: 95,
    mileage: 98430,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(30), createdBy: 'system',
    updatedAt: msAgo(8, 0), updatedBy: 'system',
  },
];

// ── Trailers (assets collection) ──────────────────────────────────────────────
export const KAIROS_DEMO_TRAILERS = [
  {
    id: 'rtr-3007-kd', tenantId: TID,
    assetNumber: 'RTR-3007', type: 'reefer_trailer',
    make: 'Wabash', year: 2022, length: '53ft',
    reeferUnit: 'Thermo King T-1200R',
    status: 'in_use', condition: 'excellent',
    currentLocation: 'Houston Yard',
    assignedLoadId: 'WC-10255',
    lastInspection: msDaysAgo(14),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(120), createdBy: 'system',
    updatedAt: msDaysAgo(14), updatedBy: 'system',
  },
  {
    id: 'rtr-3008-kd', tenantId: TID,
    assetNumber: 'RTR-3008', type: 'reefer_trailer',
    make: 'Utility', year: 2023, length: '53ft',
    reeferUnit: 'Carrier Supra 1250',
    status: 'in_use', condition: 'good',
    currentLocation: 'Phoenix Hub',
    assignedLoadId: 'WC-10265',
    lastInspection: msDaysAgo(7),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msDaysAgo(7), updatedBy: 'system',
  },
  {
    id: 'tr-4418-kd', tenantId: TID,
    assetNumber: 'TR-4418', type: 'dry_trailer',
    make: 'Great Dane', year: 2021, length: '53ft',
    reeferUnit: null,
    status: 'available', condition: 'good',
    currentLocation: 'Dallas Terminal',
    assignedLoadId: null,
    lastInspection: msDaysAgo(30),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(200), createdBy: 'system',
    updatedAt: msDaysAgo(30), updatedBy: 'system',
  },
  {
    id: 'tr-4419-kd', tenantId: TID,
    assetNumber: 'TR-4419', type: 'dry_trailer',
    make: 'Hyundai Translead', year: 2022, length: '53ft',
    reeferUnit: null,
    status: 'available', condition: 'excellent',
    currentLocation: 'Nashville DC',
    assignedLoadId: null,
    lastInspection: msDaysAgo(21),
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(150), createdBy: 'system',
    updatedAt: msDaysAgo(21), updatedBy: 'system',
  },
];

// ── Tracker pucks reformatted for DeviceManagement (trackerSerial, lastReading) ──
export const KAIROS_DEMO_DM_TRACKERS = KAIROS_DEMO_TRACKERS.map(t => ({
  id: t.id, tenantId: TID,
  trackerSerial: t.serial,
  trackerId: t.trackerId,
  type: t.type,
  status: t.status,
  loadNumber: t.loadId,
  lastReading: {
    temperature: t.sensorReadings.temperature?.value ?? null,
    humidity: t.sensorReadings.humidity?.percent ?? null,
    battery: t.batteryPercent,
    lat: t.currentLocation.lat,
    lng: t.currentLocation.lng,
  },
  lastEventTime: t.lastHeartbeat,
  softDeleted: null,
  isDeleted: false, version: 1, source: 'seed',
  createdAt: t.createdAt, createdBy: 'system',
  updatedAt: t.updatedAt, updatedBy: 'system',
}));

// ── 24h Sensor Readings for IOTK-001 (144 readings at 10-min intervals) ──────
// Scenario: Frozen Wagyu Beef, Houston → Lake Nona, FL
// setpoint: 28–36°F. Breach at T+14:32 (peak 52°F). Recovery at T+15:48.
// Shock spike at T+14:18 (0.82g, door cycling). Light event at T+16:02 (14 lux).
export function generateIotk001Events() {
  const events = [];
  const now = Date.now();
  const startTime = now - 24 * 3600000;

  // GPS: Houston (29.76, -95.37) → Lake Nona, FL (28.40, -81.25)
  const startLat = 29.7604, startLng = -95.3698;
  const endLat = 28.3972, endLng = -81.2498;

  for (let i = 0; i < 144; i++) {
    const t = new Date(startTime + i * 600000);
    const elapsed = i / 143; // 0 = 24h ago, 1 = now
    // Progress in elapsed hours (0 to 24)
    const h = (i / 143) * 24;

    // GPS interpolation
    const lat = startLat + (endLat - startLat) * elapsed;
    const lng = startLng + (endLng - startLng) * elapsed;

    // Temperature: normal 28–32°F baseline
    // Breach window: 14:18 → 15:48 (i ~85 → i ~101 out of 143)
    // Peak 52°F at 14:32 (i ~87)
    const breachStart = 85, breachPeak = 87, breachEnd = 101;
    let temp;
    if (i < breachStart) {
      temp = 29 + (Math.sin(i * 0.3) * 1.5); // normal fluctuation 27.5–30.5
    } else if (i <= breachPeak) {
      // rise to 52°F
      const progress = (i - breachStart) / (breachPeak - breachStart);
      temp = 29 + progress * (52 - 29);
    } else if (i <= breachEnd) {
      // hold high then recover
      const recoverProgress = (i - breachPeak) / (breachEnd - breachPeak);
      temp = 52 - recoverProgress * (52 - 30);
    } else {
      // post-recovery normal
      temp = 29.5 + (Math.sin(i * 0.2) * 1.2);
    }
    temp = Math.round(temp * 10) / 10;

    // Humidity: 58–72% baseline
    const humidity = Math.round(62 + Math.sin(i * 0.15) * 7);

    // Shock: baseline 0.05–0.15g, spike at i ~84 (T+14:18)
    const shockSpike = i === 84;
    const shock = shockSpike ? 0.82 : Math.round((0.07 + Math.random() * 0.09) * 100) / 100;

    // Light: 0 baseline, event at i ~97 (T+16:02)
    const lightEvent = i === 97;
    const light = lightEvent ? 14 : 0;

    const alertTriggered = temp > 36 || shockSpike;

    events.push({
      id: `iotk001-r-${i}`,
      tenantId: TID,
      deviceId: 'IOTK-001',
      trackerId: 'IOTK-001',
      readingId: `r-iotk001-${i}`,
      loadId: 'WC-10255',
      timestamp: t.toISOString(),
      temperature: temp,
      tempF: temp,
      tempUnit: 'F',
      tempMin: 28,
      tempMax: 36,
      humidity,
      humidityPct: humidity,
      humidityMin: 58,
      humidityMax: 72,
      shock,
      gForce: shock,
      shockG: shock,
      shockSeverity: shockSpike ? 'significant' : 'none',
      light,
      lightStatus: lightEvent ? 'unauthorized_open' : 'sealed',
      doorOpen: lightEvent,
      gps: { lat: Math.round(lat * 10000) / 10000, lng: Math.round(lng * 10000) / 10000 },
      lat: Math.round(lat * 10000) / 10000,
      lng: Math.round(lng * 10000) / 10000,
      speed: i < 5 ? 0 : Math.round(58 + Math.sin(i * 0.4) * 12),
      setpoint: 32,
      ambient: Math.round(75 + Math.sin(i * 0.1) * 8),
      alertTriggered,
      softDeleted: null,
      isDeleted: false, version: 1, source: 'seed',
      createdAt: t.toISOString(), createdBy: 'system',
    });
  }
  return events;
}

export const KAIROS_DEMO_TRACKER_EVENTS = {
  'IOTK-001': generateIotk001Events(),
};

// ── IoT Thresholds (8 configurations) ────────────────────────────────────────
export const KAIROS_DEMO_THRESHOLDS = [
  // Pharma (3 configs)
  {
    id: 'thr-pharma-001', tenantId: TID,
    assetId: 'rtr-3007-kd', assetLabel: 'RTR-3007',
    shipmentType: 'pharma',
    temperatureFMin: -10, temperatureFMax: 32,
    humidityPctMin: 50, humidityPctMax: 80,
    shockG: 1.5, idleMinutes: 30,
    escalation: 'immediate',
    tempMin: -10, tempMax: 32, humidityMin: 50, humidityMax: 80,
    shockThreshold: 1.5, lightMax: 0,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(10), createdBy: 'system',
    updatedAt: msDaysAgo(10), updatedBy: 'system',
  },
  {
    id: 'thr-pharma-002', tenantId: TID,
    assetId: 'iotk-003-kd', assetLabel: 'IOTK-003',
    shipmentType: 'pharma',
    temperatureFMin: 35, temperatureFMax: 46,
    humidityPctMin: 30, humidityPctMax: 60,
    shockG: 1.5, idleMinutes: 30,
    escalation: 'avg_5min',
    tempMin: 35, tempMax: 46, humidityMin: 30, humidityMax: 60,
    shockThreshold: 1.5, lightMax: 0,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(8), createdBy: 'system',
    updatedAt: msDaysAgo(8), updatedBy: 'system',
  },
  {
    id: 'thr-pharma-003', tenantId: TID,
    assetId: 'rtr-3008-kd', assetLabel: 'RTR-3008',
    shipmentType: 'pharma',
    temperatureFMin: 36, temperatureFMax: 46,
    humidityPctMin: 40, humidityPctMax: 65,
    shockG: 1.5, idleMinutes: 30,
    escalation: 'immediate',
    tempMin: 36, tempMax: 46, humidityMin: 40, humidityMax: 65,
    shockThreshold: 1.5, lightMax: 0,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(5), createdBy: 'system',
    updatedAt: msDaysAgo(5), updatedBy: 'system',
  },
  // Food & Beverage (3 configs)
  {
    id: 'thr-fnb-001', tenantId: TID,
    assetId: 'iotk-001-kd', assetLabel: 'IOTK-001',
    shipmentType: 'food_beverage',
    temperatureFMin: 28, temperatureFMax: 36,
    humidityPctMin: 58, humidityPctMax: 72,
    shockG: 2.0, idleMinutes: 45,
    escalation: 'immediate',
    tempMin: 28, tempMax: 36, humidityMin: 58, humidityMax: 72,
    shockThreshold: 2.0, lightMax: 5,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(3), createdBy: 'system',
    updatedAt: msDaysAgo(3), updatedBy: 'system',
  },
  {
    id: 'thr-fnb-002', tenantId: TID,
    assetId: 'iotk-004-kd', assetLabel: 'IOTK-004',
    shipmentType: 'food_beverage',
    temperatureFMin: 34, temperatureFMax: 40,
    humidityPctMin: 70, humidityPctMax: 95,
    shockG: 2.0, idleMinutes: 45,
    escalation: 'sustained',
    tempMin: 34, tempMax: 40, humidityMin: 70, humidityMax: 95,
    shockThreshold: 2.0, lightMax: 5,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(6), createdBy: 'system',
    updatedAt: msDaysAgo(6), updatedBy: 'system',
  },
  {
    id: 'thr-fnb-003', tenantId: TID,
    assetId: 'tr-4418-kd', assetLabel: 'TR-4418',
    shipmentType: 'food_beverage',
    temperatureFMin: 33, temperatureFMax: 40,
    humidityPctMin: 40, humidityPctMax: 70,
    shockG: 2.0, idleMinutes: 60,
    escalation: 'avg_5min',
    tempMin: 33, tempMax: 40, humidityMin: 40, humidityMax: 70,
    shockThreshold: 2.0, lightMax: 50,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(12), createdBy: 'system',
    updatedAt: msDaysAgo(12), updatedBy: 'system',
  },
  // General (2 configs)
  {
    id: 'thr-gen-001', tenantId: TID,
    assetId: 'dt-2018-kd', assetLabel: 'DT-2018',
    shipmentType: 'general',
    temperatureFMin: 32, temperatureFMax: 70,
    humidityPctMin: 0, humidityPctMax: 95,
    shockG: 3.0, idleMinutes: 60,
    escalation: 'immediate',
    tempMin: 32, tempMax: 70, humidityMin: 0, humidityMax: 95,
    shockThreshold: 3.0, lightMax: 500,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(15), createdBy: 'system',
    updatedAt: msDaysAgo(15), updatedBy: 'system',
  },
  {
    id: 'thr-gen-002', tenantId: TID,
    assetId: 'tr-4419-kd', assetLabel: 'TR-4419',
    shipmentType: 'general',
    temperatureFMin: 32, temperatureFMax: 70,
    humidityPctMin: 0, humidityPctMax: 95,
    shockG: 3.0, idleMinutes: 60,
    escalation: 'avg_5min',
    tempMin: 32, tempMax: 70, humidityMin: 0, humidityMax: 95,
    shockThreshold: 3.0, lightMax: 500,
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(20), createdBy: 'system',
    updatedAt: msDaysAgo(20), updatedBy: 'system',
  },
];

// ── IoT Alerts ────────────────────────────────────────────────────────────────
// IOT-AEGIS-DEMO-READINESS-1 demo-narrative shape:
//   2 BREACH alerts with carrierOutreachDraft pre-staged (operator can send/edit)
//   3 AEGIS-PREDICTED alerts (aegisPredicted:true, predictionConfidence:0.87)
//   plus existing nominal + investigative fixtures
export const KAIROS_DEMO_ALERTS = [
  // ── 2 BREACH with carrier-outreach drafts ──────────────────────────────────
  {
    id: 'alrt-kd-01', tenantId: TID,
    deviceId: 'IOTK-001', state: 'temp_excursion',
    severity: 'critical',
    assetType: 'Reefer', load: 'WC-10255',
    temperature: 52.0, threshold: 36,
    ts: msAgo(0, 28),
    aegisPredicted: false,
    carrierOutreachDraft: {
      channel: 'sms',
      carrierScac: 'SWFT',
      carrierName: 'Swift Logistics',
      recipientName: 'Dispatch — Swift',
      recipientPhone: '+1-555-0142',
      subject: 'URGENT: Temp excursion WC-10255 (Wagyu Beef)',
      body: 'IOTK-001 reading 52°F (setpoint max 36°F) for 28 min. Customer: Wild Fork Foods. Need confirmed reefer status + ETA correction within 15 min or we initiate cargo recovery protocol. Reply CONFIRM/STATUS.',
      draftedBy: 'aegis',
      draftedAt: msAgo(0, 26),
      status: 'awaiting_approval',
    },
    workflowStatus: 'assigned', assignee: 'dispatch', notes: [],
  },
  {
    id: 'alrt-kd-02', tenantId: TID,
    deviceId: 'RTR-3007', state: 'temp_excursion',
    severity: 'critical',
    assetType: 'Reefer Trailer', load: 'WC-10702',
    temperature: 48.5, threshold: 36,
    ts: msAgo(2, 10),
    aegisPredicted: false,
    carrierOutreachDraft: {
      channel: 'email',
      carrierScac: 'KNGT',
      carrierName: 'Knight Transportation',
      recipientName: 'Knight Ops Center',
      recipientEmail: 'ops-tx@knight-demo.test',
      subject: 'Temperature breach — RTR-3007 / Load WC-10702',
      body: 'RTR-3007 trailer reefer is reading 48.5°F (setpoint 36°F max) over the last 2h. Driver D. Martinez, currently I-10 corridor. Please confirm reefer unit diagnostic + dispatch yard-check at next stop. SLA penalty exposure: $24,000.',
      draftedBy: 'aegis',
      draftedAt: msAgo(2, 7),
      status: 'awaiting_approval',
    },
    workflowStatus: 'investigating', assignee: 'ops-lead', notes: [],
  },
  // ── Device-offline (still critical, no carrier outreach) ──────────────────
  {
    id: 'alrt-kd-03', tenantId: TID,
    deviceId: 'IOTK-004', state: 'device_offline',
    severity: 'critical',
    assetType: 'Tracker Puck', load: 'WC-10247',
    lastPing: '1h 22m',
    ts: msAgo(1, 22),
    aegisPredicted: false,
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  // ── 3 AEGIS-PREDICTED (predictive, not yet breached) ──────────────────────
  {
    id: 'alrt-kd-04', tenantId: TID,
    deviceId: 'IOTK-003', state: 'threshold_approaching',
    severity: 'warning',
    assetType: 'Pharma Reefer', load: 'WC-10265',
    metric: 'Temperature', value: 44.8, limit: 46,
    ts: msAgo(0, 45),
    aegisPredicted: true,
    predictionConfidence: 0.87,
    predictionWindowMinutes: 38,
    predictionModel: 'aegis-thermal-v2',
    predictionRationale: 'Temperature slope +0.21°F/min sustained 12 min + ambient rising — projected breach in 38 min absent intervention.',
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-11', tenantId: TID,
    deviceId: 'IOTK-006', state: 'route_deviation_predicted',
    severity: 'warning',
    assetType: 'Dry Van Multi-Sensor', load: 'WC-10283',
    metric: 'Route', value: 14.2, limit: 5,
    ts: msAgo(0, 18),
    aegisPredicted: true,
    predictionConfidence: 0.87,
    predictionWindowMinutes: 90,
    predictionModel: 'aegis-route-v1',
    predictionRationale: 'GPS 14.2 mi off plan + speed reduction — likely re-route via I-25; projected 90-min late delivery to Whole Foods Denver.',
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-12', tenantId: TID,
    deviceId: 'IOTK-005', state: 'battery_decline_predicted',
    severity: 'warning',
    assetType: 'Reefer Multi-Sensor', load: 'WC-10271',
    metric: 'Battery', value: 81, limit: 20,
    ts: msAgo(0, 22),
    aegisPredicted: true,
    predictionConfidence: 0.87,
    predictionWindowMinutes: 720,
    predictionModel: 'aegis-battery-v1',
    predictionRationale: 'Discharge rate 5.8%/hr vs baseline 2.1%/hr — battery projected <20% in 12h; swap before Memphis crossdock.',
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  // ── Shock event (residual at-risk, non-predicted) ─────────────────────────
  {
    id: 'alrt-kd-05', tenantId: TID,
    deviceId: 'IOTK-001', state: 'shock_impact',
    severity: 'warning',
    assetType: 'Reefer Puck', load: 'WC-10255',
    gForce: 0.82,
    ts: msAgo(0, 42),
    aegisPredicted: false,
    workflowStatus: 'assigned', assignee: 'safety', notes: [],
  },
  // Pending / Nominal (5)
  {
    id: 'alrt-kd-06', tenantId: TID,
    deviceId: 'IOTK-002', state: 'sensor_nominal',
    assetType: 'Dry Puck', load: 'WC-10262',
    temperature: 68.4,
    ts: msAgo(0, 5),
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-07', tenantId: TID,
    deviceId: 'RT-1042', state: 'sensor_nominal',
    assetType: 'Reefer Truck',
    temperature: 31.2,
    ts: msAgo(0, 8),
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-08', tenantId: TID,
    deviceId: 'RT-1043', state: 'sensor_nominal',
    assetType: 'Reefer Truck',
    temperature: 39.1,
    ts: msAgo(0, 12),
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-09', tenantId: TID,
    deviceId: 'RTR-3008', state: 'new_device',
    assetType: 'Reefer Trailer', serial: 'KD-SN-3008',
    ts: msAgo(6, 0),
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
  {
    id: 'alrt-kd-10', tenantId: TID,
    deviceId: 'DT-2019', state: 'dwell_alert',
    assetType: 'Dry Truck',
    dwellHrs: 8.4, expectedHrs: 4,
    ts: msAgo(8, 0),
    workflowStatus: 'new', assignee: 'unassigned', notes: [],
  },
];

// ── IoT AOPs (Autonomous Operating Policies, iot_aops collection) ────────────
// IOT-AEGIS-DEMO-READINESS-1: 5 active policies covering the demo narrative
// (temperature, humidity, geo-fence, battery, connectivity).
export const KAIROS_DEMO_AOPS = [
  {
    id: 'aop-kd-temp-001', tenantId: TID,
    aopId: 'aop-kd-temp-001',
    name: 'Reefer temperature breach — auto-page dispatch',
    description: 'If reefer temp exceeds setpoint max for 5 sustained minutes, page dispatch + draft carrier SMS.',
    naturalLanguage: 'When reefer temperature stays above the setpoint maximum for 5 minutes, page dispatch and draft a carrier SMS.',
    structuredDsl: null,
    ast: {
      type: 'aop',
      conditions: [
        { field: 'temperature', op: '>', value: 36, sustainedMinutes: 5, assetClass: 'reefer' },
      ],
      actions: [
        { type: 'page', target: 'dispatch' },
        { type: 'draft_carrier_outreach', channel: 'sms' },
      ],
      scope: { customerSegments: ['food_beverage', 'pharma'] },
      rawDsl: null,
    },
    policy: { version: '1.0', logic: 'AND' },
    version: 2, status: 'active', authorId: 'aegis-bootstrap',
    createdAt: Date.now() - 14 * 86400000, updatedAt: Date.now() - 2 * 86400000,
    softDeleted: false,
  },
  {
    id: 'aop-kd-humidity-002', tenantId: TID,
    aopId: 'aop-kd-humidity-002',
    name: 'Pharma humidity ceiling — escalate to ops',
    description: 'Pharma cold-chain humidity >65% for 10 min escalates to ops lead.',
    naturalLanguage: 'When pharma humidity exceeds 65% for 10 minutes, escalate to ops lead.',
    structuredDsl: null,
    ast: {
      type: 'aop',
      conditions: [
        { field: 'humidity', op: '>', value: 65, sustainedMinutes: 10, shipmentType: 'pharma' },
      ],
      actions: [
        { type: 'escalate', target: 'ops-lead' },
      ],
      scope: { customerSegments: ['pharma'] },
      rawDsl: null,
    },
    policy: { version: '1.0', logic: 'AND' },
    version: 1, status: 'active', authorId: 'aegis-bootstrap',
    createdAt: Date.now() - 10 * 86400000, updatedAt: Date.now() - 10 * 86400000,
    softDeleted: false,
  },
  {
    id: 'aop-kd-geo-003', tenantId: TID,
    aopId: 'aop-kd-geo-003',
    name: 'Geo-fence exit — notify customer success',
    description: 'Asset leaves planned-route corridor by >10 miles → CS notification.',
    naturalLanguage: 'If a tracker exits the planned route by more than 10 miles, notify customer success.',
    structuredDsl: null,
    ast: {
      type: 'aop',
      conditions: [
        { field: 'route_offset_miles', op: '>', value: 10, sustainedMinutes: 3 },
      ],
      actions: [
        { type: 'notify', target: 'customer-success' },
        { type: 'create_exception', code: 'GEO_FENCE_EXIT' },
      ],
      scope: { customerSegments: ['all'] },
      rawDsl: null,
    },
    policy: { version: '1.0', logic: 'AND' },
    version: 1, status: 'active', authorId: 'aegis-bootstrap',
    createdAt: Date.now() - 8 * 86400000, updatedAt: Date.now() - 8 * 86400000,
    softDeleted: false,
  },
  {
    id: 'aop-kd-battery-004', tenantId: TID,
    aopId: 'aop-kd-battery-004',
    name: 'Battery critical — schedule swap at next stop',
    description: 'Tracker battery <20% drafts a swap task at the next planned stop.',
    naturalLanguage: 'When tracker battery drops below 20%, create a swap task at the next planned stop.',
    structuredDsl: null,
    ast: {
      type: 'aop',
      conditions: [
        { field: 'batteryPercent', op: '<', value: 20 },
      ],
      actions: [
        { type: 'create_task', taskType: 'tracker_swap', assignedTo: 'yard-ops' },
      ],
      scope: { customerSegments: ['all'] },
      rawDsl: null,
    },
    policy: { version: '1.0', logic: 'AND' },
    version: 1, status: 'active', authorId: 'aegis-bootstrap',
    createdAt: Date.now() - 6 * 86400000, updatedAt: Date.now() - 6 * 86400000,
    softDeleted: false,
  },
  {
    id: 'aop-kd-conn-005', tenantId: TID,
    aopId: 'aop-kd-conn-005',
    name: 'Connectivity loss — predictive escalation',
    description: 'If a device misses heartbeats for >30 minutes in-transit, escalate as offline and request carrier visual check.',
    naturalLanguage: 'If a device misses heartbeats for more than 30 minutes while in transit, escalate as offline and request a carrier visual check.',
    structuredDsl: null,
    ast: {
      type: 'aop',
      conditions: [
        { field: 'lastPingMinutes', op: '>', value: 30, loadStatus: 'in_transit' },
      ],
      actions: [
        { type: 'escalate', target: 'dispatch' },
        { type: 'request_visual_check', target: 'carrier' },
      ],
      scope: { customerSegments: ['all'] },
      rawDsl: null,
    },
    policy: { version: '1.0', logic: 'AND' },
    version: 1, status: 'active', authorId: 'aegis-bootstrap',
    createdAt: Date.now() - 4 * 86400000, updatedAt: Date.now() - 4 * 86400000,
    softDeleted: false,
  },
];

// ── ELD / HOS Demo Drivers (fleet_drivers collection) ─────────────────────────
// DOT compliance: 96%. HOS violations last 30d: 1.
export const KAIROS_DEMO_ELD_DRIVERS = [
  {
    id: 'drv-kd-01', tenantId: TID,
    name: 'D. Martinez', driverId: 'DOT-44218',
    hosStatus: 'driving',
    drivingHoursRemaining: 1.8,
    onDutyHoursRemaining: 4.5,
    cycleHoursRemaining: 36.2,
    riskLevel: 'critical',
    currentLoadId: 'WC-10702',
    lastSyncAt: msAgo(0, 5),
    violationsLast30d: 1,
    provider: 'samsara_demo',
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msAgo(0, 5), updatedBy: 'system',
  },
  {
    id: 'drv-kd-02', tenantId: TID,
    name: 'K. Patterson', driverId: 'DOT-44219',
    hosStatus: 'on_duty',
    drivingHoursRemaining: 5.9,
    onDutyHoursRemaining: 5.9,
    cycleHoursRemaining: 49.1,
    riskLevel: 'ok',
    currentLoadId: 'WC-10698',
    lastSyncAt: msAgo(0, 12),
    violationsLast30d: 0,
    provider: 'samsara_demo',
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msAgo(0, 12), updatedBy: 'system',
  },
  {
    id: 'drv-kd-03', tenantId: TID,
    name: 'J. Rivera', driverId: 'DOT-44220',
    hosStatus: 'driving',
    drivingHoursRemaining: 3.2,
    onDutyHoursRemaining: 6.7,
    cycleHoursRemaining: 58.5,
    riskLevel: 'warning',
    currentLoadId: 'WC-10711',
    lastSyncAt: msAgo(0, 8),
    violationsLast30d: 0,
    provider: 'samsara_demo',
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msAgo(0, 8), updatedBy: 'system',
  },
  {
    id: 'drv-kd-04', tenantId: TID,
    name: 'M. Chen', driverId: 'DOT-44221',
    hosStatus: 'off_duty',
    drivingHoursRemaining: 11.0,
    onDutyHoursRemaining: 14.0,
    cycleHoursRemaining: 70.0,
    riskLevel: 'ok',
    currentLoadId: null,
    lastSyncAt: msAgo(8, 0),
    violationsLast30d: 0,
    provider: 'samsara_demo',
    softDeleted: null,
    isDeleted: false, version: 1, source: 'seed',
    createdAt: msDaysAgo(90), createdBy: 'system',
    updatedAt: msAgo(8, 0), updatedBy: 'system',
  },
];

// HOS summary computed from demo drivers
export const KAIROS_DEMO_HOS_SUMMARY = {
  complianceRate: 0.96,
  driversNearLimit: [
    { driverId: 'DOT-44218', name: 'D. Martinez', driveRemaining: 1.8, riskLevel: 'critical' },
    { driverId: 'DOT-44220', name: 'J. Rivera', driveRemaining: 3.2, riskLevel: 'warning' },
  ],
  totalDrivers: 4,
  criticalCount: 1,
  warningCount: 1,
  violationsLast30d: 1,
};

// ── Digital Twin asset model for IOTK-001 ──────────────────────────────────
export const KAIROS_DEMO_TWIN_IOTK001 = {
  assetId: 'iotk-001-kd',
  tenantId: TID,
  telemetry: {
    temperatureF: 29.4,
    humidityPct: 67,
    shockG: 0.09,
    batteryPct: 78,
    doorOpen: false,
    gps: { lat: 28.5383, lng: -81.3792 },
    speed: 62,
  },
  doorStateTimeline: [
    { state: 'closed', from: null, to: msAgo(7, 58) },
    { state: 'opened', from: msAgo(7, 58), to: msAgo(7, 55) },
    { state: 'closed', from: msAgo(7, 55), to: null },
  ],
  anomalies: [
    {
      type: 'temp_excursion',
      severity: 'critical',
      detail: 'Temperature reached 52.0°F at T+14:32 (setpoint max 36°F). Door-open event correlated at T+14:18.',
      occurredAt: msAgo(9, 28),
      recommendedAction: 'Verify reefer unit functionality. Inspect cargo integrity at next stop.',
    },
  ],
  recentEvents: [
    { type: 'shock_event', eventType: 'shock_event', occurredAt: msAgo(9, 42), timestamp: msAgo(9, 42), location: 'I-10 near Beaumont, TX', detail: '0.82g shock detected — door cycling' },
    { type: 'temp_breach', eventType: 'temp_breach', occurredAt: msAgo(9, 28), timestamp: msAgo(9, 28), location: 'I-10 near Lake Charles, LA', detail: 'Temperature exceeded setpoint max (52°F > 36°F)' },
    { type: 'door_open', eventType: 'door_open', occurredAt: msAgo(7, 58), timestamp: msAgo(7, 58), location: 'I-10 near Beaumont, TX', detail: '14 lux detected — unauthorized open' },
    { type: 'door_close', eventType: 'door_close', occurredAt: msAgo(7, 55), timestamp: msAgo(7, 55), location: 'I-10 near Beaumont, TX', detail: 'Door closed — light returned to 0 lux' },
    { type: 'heartbeat', eventType: 'heartbeat', occurredAt: msAgo(0, 8), timestamp: msAgo(0, 8), location: 'Orlando, FL', detail: 'Normal heartbeat — all sensors nominal' },
  ],
};
